/**
 * Version History panel — lists immutable NetworkRelease snapshots for a network.
 *
 * Backed by GET /networks/{id}/releases. Read-only; selecting a row calls
 * ``onViewRelease(version)`` so the parent can open a detail drawer if desired.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { listNetworkReleases } from '@/services/networkReleasesApi';
import { formatRelativeTime } from '@/utils/formatDate';
import { displayVersionLabel } from '@stepflow/ui-shared/utils/stepflowVersionLabel';

export default function ReleaseVersionHistory({ networkId, onViewRelease, defaultOpen = false }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [releases, setReleases] = useState([]);
  const [reloadTick, setReloadTick] = useState(0);

  // LAZY: the releases list is an overhead read — only fetch once the panel is
  // actually opened (and on explicit Refresh). Collapsed by default.
  useEffect(() => {
    if (!networkId || !open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listNetworkReleases(networkId, { limit: 50 })
      .then((res) => {
        if (cancelled) return;
        setReleases(Array.isArray(res.releases) ? res.releases : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load release history');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [networkId, open, reloadTick]);

  const reload = useCallback(() => setReloadTick((n) => n + 1), []);

  const sorted = useMemo(
    () => [...releases].sort((a, b) => (b.releaseVersion || 0) - (a.releaseVersion || 0)),
    [releases],
  );

  if (!networkId) return null;

  return (
    <div className="rounded-md border border-gray-200 bg-white">
      <div className="flex items-center justify-between px-3 py-2">
        {/* The header IS the collapse toggle — one "Version history" control, no wrapper. */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex items-center gap-1.5 text-sm font-semibold text-gray-800"
        >
          <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden
            className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${open ? 'rotate-90' : ''}`}
          >
            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
          </svg>
          Version history
        </button>
        {open ? (
          <button
            type="button"
            onClick={reload}
            className="text-xs text-gray-500 hover:text-gray-800"
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        ) : null}
      </div>

      {open && error && (
        <div className="border-t border-gray-200 px-3 py-2 text-xs text-red-600" role="alert">
          {error}
        </div>
      )}

      {open && loading && sorted.length === 0 && (
        <div className="border-t border-gray-200 px-3 py-4 text-xs text-gray-500">Loading…</div>
      )}

      {open && !loading && !error && sorted.length === 0 && (
        <div className="border-t border-gray-200 px-3 py-4 text-xs text-gray-500">
          No releases yet. Release this network to create the first immutable snapshot.
        </div>
      )}

      {open && sorted.length > 0 && (
        <ul className="divide-y divide-gray-100 border-t border-gray-200" role="list">
          {sorted.map((r) => (
            <li key={r.releaseVersion} className="flex items-center justify-between px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900">
                  {displayVersionLabel(r, sorted) || `Release ${r.releaseVersion}`}
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    {formatRelativeTime(r.releasedAt)}
                  </span>
                </div>
                <div className="truncate text-xs text-gray-500" title={r.releasedBy}>
                  Released by {r.releasedBy || 'unknown'}
                </div>
              </div>
              {typeof onViewRelease === 'function' && (
                <button
                  type="button"
                  onClick={() => onViewRelease(r.releaseVersion)}
                  className="shrink-0 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                >
                  View snapshot
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
